/**
 * Silent re-authentication — the client half (the server half is
 * app/server/src/session.ts, /api/auth/reauth).
 *
 * Google deletes an OAuth client that has run no Sign-in with Google flow for
 * ~5 months, and our session cookie lives 30 years, so the client sat unused
 * and Google scheduled its deletion (Nov 2026). Once a session is older than
 * REAUTH_AFTER_DAYS this module sends the browser through
 * /api/auth/reauth → Google (`prompt=none`) → back to the same page: a
 * sub-second hop that counts as a sign-in and mints a fresh cookie. It is
 * attempted only on a top-level load, only online, at most once per
 * REAUTH_RETRY_DAYS per browser (localStorage, so a hop that keeps failing
 * for any reason can never loop), and never while the server's back-off from
 * a previous failed attempt (`reauthSkipUntil`, 30 days) is in force.
 */

/** A session older than this triggers the hop — comfortably inside Google's
 * ~5-month inactivity window as long as ANY device re-auths. */
export const REAUTH_AFTER_DAYS = 120
/** Minimum spacing between attempts on one browser, whatever the outcome. */
export const REAUTH_RETRY_DAYS = 7
export const REAUTH_ATTEMPT_KEY = 'auth.reauthAttemptedAt'

const DAY_MS = 86_400_000

export interface ReauthInput {
  /** The session's issuedAt (epoch ms); undefined for a legacy cookie, which
   * is treated as old (the hop upgrades it). */
  issuedAt?: number | null
  /** Server back-off from a failed silent attempt (epoch ms), if any. */
  reauthSkipUntil?: number | null
  /** This browser's last attempt (epoch ms), if any. */
  lastAttempt?: number | null
  online: boolean
  now?: number
}

/** The decision, kept pure so it can be reasoned about: hop when the session
 * is old enough, we are online, no back-off is in force, and this browser
 * has not tried within REAUTH_RETRY_DAYS. */
export function shouldSilentReauth({ issuedAt, reauthSkipUntil, lastAttempt, online, now = Date.now() }: ReauthInput): boolean {
  if (!online) return false
  if (reauthSkipUntil != null && reauthSkipUntil > now) return false
  if (lastAttempt != null && now - lastAttempt < REAUTH_RETRY_DAYS * DAY_MS) return false
  if (issuedAt != null && now - issuedAt < REAUTH_AFTER_DAYS * DAY_MS) return false
  return true
}

export function readLastAttempt(): number | null {
  try {
    const raw = window.localStorage.getItem(REAUTH_ATTEMPT_KEY)
    const n = raw == null ? NaN : Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

/** Called with a freshly fetched session. Returns true when it navigated. */
export function maybeSilentReauth(session: { issuedAt?: number; reauthSkipUntil?: number } | null | undefined): boolean {
  if (!session || typeof window === 'undefined') return false
  const input: ReauthInput = {
    issuedAt: session.issuedAt,
    reauthSkipUntil: session.reauthSkipUntil,
    lastAttempt: readLastAttempt(),
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
  }
  if (!shouldSilentReauth(input)) return false
  try {
    window.localStorage.setItem(REAUTH_ATTEMPT_KEY, String(Date.now()))
  } catch {
    // No storage (private mode with storage blocked): still hop once; the
    // server's back-off covers a failing device after that.
  }
  const cb = window.location.pathname + window.location.search
  window.location.replace(`/api/auth/reauth?callbackUrl=${encodeURIComponent(cb)}`)
  return true
}
